import { Alert, Button, Grid, Space, Table, Tooltip, Typography } from "antd";
import type { TableProps } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  useAttendanceSummary,
  type AttendanceCounts,
  type AttendanceReportSort,
  type ClassAttendanceSummary,
  type ReportRange,
  type StudentAttendanceSummary,
  type SummaryKind,
  type TeacherAttendanceSummary,
} from "../app/reports.ts";

type Row = StudentAttendanceSummary | ClassAttendanceSummary | TeacherAttendanceSummary;
type SortableKey = "name" | "present" | "absent" | "unmarked";

const DEFAULT_PAGE_SIZE = 25;
const sortByKey: Record<SortableKey, AttendanceReportSort> = { name: "Name", present: "PresentPercent", absent: "AbsentPercent", unmarked: "Unmarked" };

interface Props {
  kind: SummaryKind;
  range: ReportRange;
  enabled: boolean;
}

/** A percentage from the API (one decimal) in Uzbek notation, for example "62,5%". */
export function formatPercent(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${Number(value).toFixed(1).replace(".", ",")}%`;
}

function rowKey(row: Row): string {
  if ("studentId" in row) return row.studentId;
  if ("classId" in row) return row.classId;
  return row.teacherId ?? "no-teacher";
}

function HeaderHint({ title, hint }: { title: string; hint: string }) {
  return (
    <Space size={4}>
      {title}
      <Tooltip title={hint}>
        <QuestionCircleOutlined aria-label={hint} style={{ color: "var(--ant-color-text-tertiary)" }} />
      </Tooltip>
    </Space>
  );
}

/** One tab of the attendance report: counts and percentages per student, class or teacher, paged and sorted by the API. */
export function AttendanceSummaryTable({ kind, range, enabled }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortKey, setSortKey] = useState<SortableKey>("name");
  const [descending, setDescending] = useState(false);
  // A new range starts again on the first page; adjusted during render, as React recommends for state derived from props.
  const rangeKey = `${range.from}/${range.to}`;
  const [pagedRange, setPagedRange] = useState(rangeKey);
  if (pagedRange !== rangeKey) {
    setPagedRange(rangeKey);
    setPage(1);
  }
  const query = useAttendanceSummary(kind, range, { page, pageSize, sort: sortByKey[sortKey], descending }, enabled);

  const sortOrder = (key: SortableKey) => (sortKey === key ? (descending ? ("descend" as const) : ("ascend" as const)) : null);

  const countCell = (count: number | string, percent?: number | string | null) => (
    <div style={{ lineHeight: 1.3 }}>
      <div>{Number(count)}</div>
      {percent !== undefined && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {formatPercent(percent)}
        </Typography.Text>
      )}
    </div>
  );

  const nameColumns: TableProps<Row>["columns"] =
    kind === "students"
      ? [
          {
            title: t("reports.attendance.columns.student"),
            key: "name",
            sorter: true,
            sortOrder: sortOrder("name"),
            width: 220,
            render: (_: unknown, row: Row) =>
              "studentId" in row ? (
                <Link to={`/students/${row.studentId}`}>
                  <Typography.Text strong>{row.fullName}</Typography.Text>
                </Link>
              ) : null,
          },
          {
            title: t("reports.attendance.columns.classes"),
            key: "classes",
            width: 120,
            render: (_: unknown, row: Row) => ("classNames" in row ? row.classNames : null),
          },
        ]
      : kind === "classes"
        ? [
            {
              title: t("reports.attendance.columns.class"),
              key: "name",
              sorter: true,
              sortOrder: sortOrder("name"),
              width: 200,
              render: (_: unknown, row: Row) =>
                "classId" in row ? (
                  <div style={{ lineHeight: 1.3 }}>
                    <Link to={`/academic/classes/${row.classId}`}>
                      <Typography.Text strong>{row.className}</Typography.Text>
                    </Link>
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {row.academicYearLabel}
                      </Typography.Text>
                    </div>
                  </div>
                ) : null,
            },
          ]
        : [
            {
              title: t("reports.attendance.columns.teacher"),
              key: "name",
              sorter: true,
              sortOrder: sortOrder("name"),
              width: 240,
              render: (_: unknown, row: Row) =>
                "teacherName" in row && row.teacherName ? (
                  <Typography.Text strong>{row.teacherName}</Typography.Text>
                ) : (
                  <Typography.Text type="secondary">{t("reports.attendance.noTeacher")}</Typography.Text>
                ),
            },
          ];

  const counts = (row: Row): AttendanceCounts => row.counts;
  const columns: TableProps<Row>["columns"] = [
    ...nameColumns,
    { title: t("reports.attendance.columns.lessons"), key: "lessons", align: "right", width: 90, render: (_: unknown, row: Row) => Number(row.lessonCount) },
    {
      title: <HeaderHint title={t("reports.attendance.columns.expected")} hint={t("reports.attendance.expectedHint")} />,
      key: "expected",
      align: "right",
      width: 100,
      render: (_: unknown, row: Row) => Number(counts(row).expected),
    },
    {
      title: t("reports.attendance.columns.present"),
      key: "present",
      align: "right",
      width: 100,
      sorter: true,
      sortOrder: sortOrder("present"),
      render: (_: unknown, row: Row) => countCell(counts(row).present, counts(row).presentPercent),
    },
    {
      title: t("reports.attendance.columns.late"),
      key: "late",
      align: "right",
      width: 100,
      render: (_: unknown, row: Row) => countCell(counts(row).late, counts(row).latePercent),
    },
    {
      title: t("reports.attendance.columns.absent"),
      key: "absent",
      align: "right",
      width: 100,
      sorter: true,
      sortOrder: sortOrder("absent"),
      render: (_: unknown, row: Row) => countCell(counts(row).absent, counts(row).absentPercent),
    },
    {
      title: t("reports.attendance.columns.excused"),
      key: "excused",
      align: "right",
      width: 100,
      render: (_: unknown, row: Row) => countCell(counts(row).excused, counts(row).excusedPercent),
    },
    {
      title: t("reports.attendance.columns.unmarked"),
      key: "unmarked",
      align: "right",
      width: 130,
      sorter: true,
      sortOrder: sortOrder("unmarked"),
      render: (_: unknown, row: Row) => {
        const unmarked = Number(counts(row).unmarked);
        return unmarked > 0 ? <Typography.Text type="warning">{unmarked}</Typography.Text> : <Typography.Text type="secondary">0</Typography.Text>;
      },
    },
  ];

  const onChange: TableProps<Row>["onChange"] = (pagination, _filters, sorter) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    const key = single?.order ? (single.columnKey as SortableKey | undefined) : undefined;
    const nextKey = key && key in sortByKey ? key : "name";
    const nextDescending = Boolean(key) && single?.order === "descend";
    if (nextKey !== sortKey || nextDescending !== descending) {
      setSortKey(nextKey);
      setDescending(nextDescending);
      setPage(1);
      return;
    }
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? DEFAULT_PAGE_SIZE);
  };

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t("reports.attendance.percentHint")}
      </Typography.Text>
      <Table<Row>
        rowKey={rowKey}
        size={screens.md ? "middle" : "small"}
        loading={query.isPending || (query.isFetching && query.isPlaceholderData)}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: t("reports.attendance.empty") }}
        scroll={{ x: 960 }}
        onChange={onChange}
        showSorterTooltip={false}
        pagination={{
          current: page,
          pageSize,
          total: Number(query.data?.totalCount ?? 0),
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100],
          hideOnSinglePage: false,
          showTotal: (total: number) => t("reports.attendance.total", { count: total }),
        }}
      />
    </Space>
  );
}
